import { create, show, useModal } from "@ebay/nice-modal-react";
import { useCallback, useMemo, useState } from "react";
import { useBlocker } from "react-router";
import { isEqual } from "lodash-es";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ModalCancelButton from "ds/components/Modal/CancelButton";
import ModalContent from "ds/components/Modal/Content";
import ModalFooter from "ds/components/Modal/Footer";
import ModalHeader from "ds/components/Modal/Header";
import ModalSimple from "ds/components/ModalSimple";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import {
  Column,
  ColumnId,
  ColumnMode,
  RenderItemContentProps,
} from "components/DragDropList/types";
import DragDropList from "components/DragDropList";
import DragDropListDefaultItem from "components/DragDropList/DefaultItem";
import { showSimpleLeaveConfirmation } from "ds/components/LeaveConfirmationModal/Simple";

import { ActiveFiltersMap, FilterItemSettings, FiltersDictionary } from "../types";
import styles from "./styles.module.css";
import FiltersSettingModalItemTitle from "./ItemTitle";

type FilterSettingsModalProps = {
  dictionary?: FiltersDictionary;
  activeFilters: ActiveFiltersMap;
  setFiltersOrder: (filters: FilterItemSettings[]) => void;
  filtersOrder: FilterItemSettings[];
};

const SettingModal = create(function SettingModal({
  dictionary,
  activeFilters,
  setFiltersOrder,
  filtersOrder,
}: FilterSettingsModalProps) {
  const modal = useModal();

  // we want to set order based on savedView or filters saved locally in Filters component or localStorage value
  const [localFiltersOrder, setLocalFiltersOrder] = useState(filtersOrder);

  const isDirty = useMemo(() => {
    return !isEqual(localFiltersOrder, filtersOrder);
  }, [localFiltersOrder, filtersOrder]);

  const blocker = useBlocker(Boolean(isDirty && modal.visible));

  const handleClose = async () => {
    if (isDirty) {
      await showSimpleLeaveConfirmation();
    }
  };

  const onExit = useCallback(() => {
    if (blocker.state !== "unblocked") {
      blocker.proceed?.();
    }

    modal.remove();
  }, [blocker, modal]);

  const onChange = ({ first }: { first: Column<string> }) => {
    setLocalFiltersOrder(
      first.map((item) => {
        // Find the original filter item to preserve required properties like 'name'
        const original = filtersOrder.find((f) => f.name === item.value);
        return {
          ...item,
          name: original?.name ?? "",
          visible: !!item.props?.checked,
        } as FilterItemSettings;
      })
    );
  };

  const onSelect = useCallback(
    (_: ColumnId, id: string, isSelected: boolean) => {
      const localStateOrderDuplicate = [...localFiltersOrder];
      const itemIndex = localStateOrderDuplicate.findIndex((item) => item.name === id);
      localStateOrderDuplicate[itemIndex] = {
        ...localStateOrderDuplicate[itemIndex],
        visible: isSelected,
      } as FilterItemSettings;

      setLocalFiltersOrder(localStateOrderDuplicate);
    },
    [localFiltersOrder]
  );

  const handleSelectAll = () => {
    if (allItemsChecked) {
      setLocalFiltersOrder(
        localFiltersOrder.map((filter) => ({ ...filter, visible: activeFilters.has(filter.name) }))
      );
    } else {
      setLocalFiltersOrder(localFiltersOrder.map((filter) => ({ ...filter, visible: true })));
    }
  };

  const allItemsChecked = useMemo(
    () => localFiltersOrder.every(({ visible }) => visible),
    [localFiltersOrder]
  );

  const handleApplySettings = () => {
    setFiltersOrder(localFiltersOrder);
    modal.hide();
  };

  const renderItemContent = useCallback(
    ({
      id,
      dragging,
      handleRef,
      handleListeners,
      handleAttributes,
      disabled,
      props,
    }: RenderItemContentProps<string, { checked: boolean }>) => {
      return (
        <DragDropListDefaultItem
          dragging={dragging}
          handleRef={handleRef}
          handleListeners={handleListeners}
          handleAttributes={handleAttributes}
          disabled={disabled}
          titleComponent={
            <FiltersSettingModalItemTitle
              id={id}
              checked={props.checked}
              onSelect={onSelect}
              activeFilters={activeFilters}
              dictionary={dictionary}
            />
          }
        />
      );
    },
    [dictionary, activeFilters, onSelect]
  );

  const firstColumn = useMemo(() => {
    return localFiltersOrder.map((filter) => ({
      value: filter.name,
      props: { checked: filter.visible },
    }));
  }, [localFiltersOrder]);

  if (!filtersOrder) return null;

  return (
    <ModalSimple size="large" onClose={handleClose} onExit={onExit}>
      <ModalHeader title="Manage filters" />
      <ModalContent>
        <Typography className={styles.infoText} variant="p-body3" tag="p">
          By managing filters, you can select those that you want to be displayed on the filter list
          and deselect them. In addition, you can set the order in which filters are displayed.
        </Typography>

        <Box direction="row" align="center" className={styles.actionsBar}>
          <Toggle
            variant="checkbox"
            id="select_all_filters"
            onChange={handleSelectAll}
            isSelected={allItemsChecked}
            aria-label={allItemsChecked ? "Unselect all" : "Select all"}
          >
            Select all
          </Toggle>
        </Box>

        <Box fullWidth fullHeight className={styles.filtersListContainer}>
          <Box fullWidth direction="column">
            <DragDropList
              onChange={onChange}
              onSelect={onSelect}
              columnMode={ColumnMode.Single}
              renderItemContent={renderItemContent}
              firstColumn={firstColumn}
              vertical
              verticalAxisOnly
              hasDroppableCardWrapper
            />
          </Box>
        </Box>
      </ModalContent>
      <ModalFooter>
        <ModalCancelButton />
        <Button onPress={handleApplySettings} variant="primary">
          Apply
        </Button>
      </ModalFooter>
    </ModalSimple>
  );
});

export const showSettingModal = (props: FilterSettingsModalProps) => show(SettingModal, props);
